(() => {
  if (customElements.get('sale-tide-feature')) return;

  const RESUME_DELAY = 2500;
  const AUTOPLAY_SCROLL_GUARD = 140;

  class SaleTideFeature extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.scroller = this.querySelector('[data-sale-tide-scroller]');
      this.track = this.querySelector('[data-sale-tide-track]');
      this.pauseButton = this.querySelector('[data-sale-tide-pause]');
      this.pauseLabel = this.querySelector('[data-sale-tide-pause-label]');
      this.pauseIcon = this.querySelector('[data-sale-tide-pause-icon]');
      this.playIcon = this.querySelector('[data-sale-tide-play-icon]');
      this.progress = this.querySelector('[data-sale-tide-progress]');

      if (!this.scroller || !this.track || !this.track.children.length) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.baseSpeed = Math.min(24, Math.max(8, Number.parseFloat(this.dataset.scrollSpeed) || 20));
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.track.children.length > 1;
      this.manualPaused = !this.autoplayEnabled;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.interactionPaused = false;
      this.pagePaused = document.hidden;
      this.sectionVisible = typeof IntersectionObserver === 'undefined';
      this.hasOverflow = false;
      this.pointerActive = false;
      this.activePointerId = null;
      this.normalizingScroll = false;
      this.cardGap = 0;
      this.cardStride = 0;
      this.edgeFade = 0;
      this.animationFrame = null;
      this.resumeTimer = null;
      this.lastFrameTime = null;
      this.autoplayScrollDeadline = 0;

      this.bindEvents();
      this.observeVisibility();
      this.observeSize();
      this.measure();
      this.updateProgress();
      this.updatePauseControl();
      this.syncMotion();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();

      if (this.motionChangeHandler && this.reducedMotion) {
        if (typeof this.reducedMotion.removeEventListener === 'function') {
          this.reducedMotion.removeEventListener('change', this.motionChangeHandler);
        } else if (typeof this.reducedMotion.removeListener === 'function') {
          this.reducedMotion.removeListener(this.motionChangeHandler);
        }
      }

      if (this.animationFrame !== null && this.animationFrame !== undefined) {
        window.cancelAnimationFrame(this.animationFrame);
      }
      if (this.resumeTimer !== null && this.resumeTimer !== undefined) {
        window.clearTimeout(this.resumeTimer);
      }

      this.animationFrame = null;
      this.resumeTimer = null;
      this.lastFrameTime = null;
      this.pointerActive = false;
      this.activePointerId = null;
      this.initialized = false;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.pauseButton?.addEventListener('click', () => this.togglePause(), { signal });
      this.scroller.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.scroller.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.scroller.addEventListener('wheel', (event) => this.onWheel(event), { passive: true, signal });
      this.scroller.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.scroller.addEventListener('pointerenter', (event) => this.onPointerEnter(event), { signal });
      this.scroller.addEventListener('pointerleave', (event) => this.onPointerLeave(event), { signal });

      this.addEventListener('focusin', (event) => this.onFocusIn(event), { signal });
      this.addEventListener('focusout', (event) => this.onFocusOut(event), { signal });
      this.addEventListener('click', (event) => this.onPurchaseInteraction(event), { capture: true, signal });
      this.addEventListener('submit', () => this.onSubmitInteraction(), { capture: true, signal });

      document.addEventListener('pointerup', (event) => this.onPointerEnd(event), { capture: true, signal });
      document.addEventListener('pointercancel', (event) => this.onPointerEnd(event), { capture: true, signal });
      document.addEventListener('visibilitychange', () => {
        this.pagePaused = document.hidden;
        this.syncMotion();
      }, { signal });

      this.motionChangeHandler = () => {
        this.updatePauseControl();
        this.syncMotion();
      };

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this.motionChangeHandler);
      } else if (typeof this.reducedMotion.addListener === 'function') {
        this.reducedMotion.addListener(this.motionChangeHandler);
      }
    }

    observeVisibility() {
      if (typeof IntersectionObserver === 'undefined') {
        this.sectionVisible = true;
        return;
      }

      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (!this.isConnected) return;
        this.sectionVisible = entries.some((entry) => entry.isIntersecting);
        this.syncMotion();
      }, { rootMargin: '0px 0px', threshold: 0.01 });
      this.intersectionObserver.observe(this);
    }

    observeSize() {
      if (typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.isConnected) return;
        this.measure();
        this.updateProgress();
        this.syncMotion();
      });
      this.resizeObserver.observe(this.scroller);
    }

    measure() {
      const firstCard = this.track.firstElementChild;
      const trackStyles = window.getComputedStyle(this.track);
      const scrollerStyles = window.getComputedStyle(this.scroller);
      const measuredGap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap);
      const measuredEdge = Number.parseFloat(scrollerStyles.getPropertyValue('--sale-tide-edge'));

      this.cardGap = Number.isFinite(measuredGap) ? measuredGap : 0;
      this.edgeFade = Number.isFinite(measuredEdge) ? measuredEdge : 0;
      this.cardStride = (firstCard?.offsetWidth || 0) + this.cardGap;
      this.hasOverflow = this.track.children.length > 1 && this.scroller.scrollWidth > this.scroller.clientWidth + 1;
      this.classList.toggle('is-static', !this.hasOverflow);
    }

    getResponsiveSpeed(baseSpeed = this.baseSpeed || 20) {
      if (window.innerWidth <= 749) return baseSpeed * 0.75;
      if (window.innerWidth <= 1199) return baseSpeed * 0.875;
      return baseSpeed;
    }

    shouldAnimateAtmosphere() {
      return Boolean(
        !this.pagePaused
        && this.sectionVisible
        && !this.reducedMotion?.matches
      );
    }

    syncAtmosphere() {
      this.classList.toggle('is-atmosphere-paused', !this.shouldAnimateAtmosphere());
    }

    shouldAnimate() {
      return Boolean(
        this.autoplayEnabled
        && !this.manualPaused
        && !this.hoverPaused
        && !this.focusPaused
        && !this.interactionPaused
        && !this.pointerActive
        && !this.pagePaused
        && this.sectionVisible
        && this.hasOverflow
        && !this.reducedMotion?.matches
      );
    }

    requestMotion() {
      if (!this.shouldAnimate() || this.animationFrame !== null) return;
      this.animationFrame = window.requestAnimationFrame((timestamp) => this.advanceFrame(timestamp));
    }

    stopMotion() {
      if (this.animationFrame !== null) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      this.lastFrameTime = null;
    }

    syncMotion() {
      this.syncAtmosphere();
      if (this.shouldAnimate()) {
        this.requestMotion();
      } else {
        this.stopMotion();
      }
    }

    advanceFrame(timestamp) {
      this.animationFrame = null;
      if (!this.shouldAnimate() || !this.isConnected) {
        this.lastFrameTime = null;
        return;
      }

      if (this.lastFrameTime === null) this.lastFrameTime = timestamp;
      const elapsedSinceFrame = timestamp - this.lastFrameTime;
      const elapsed = Math.min(elapsedSinceFrame, 64);
      this.lastFrameTime = timestamp;

      if (elapsed > 0) {
        const frameDistance = this.getResponsiveSpeed(this.baseSpeed) * (elapsed / 1000);
        this.autoplayScrollDeadline = Date.now() + AUTOPLAY_SCROLL_GUARD;
        this.scroller.scrollLeft += frameDistance;
        if (this.cardStride > 0 && this.scroller.scrollLeft >= this.cardStride + this.edgeFade) {
          this.recycleForward();
        }
      }

      this.requestMotion();
    }

    recycleForward() {
      if (!this.track || !this.scroller || this.normalizingScroll) return;

      this.normalizingScroll = true;
      const limit = this.track.children.length;
      let moved = 0;

      while (moved < limit) {
        const firstCard = this.track.children[0];
        if (!firstCard) break;

        const stride = (firstCard.offsetWidth || 0) + (this.cardGap || 0);
        if (stride <= 0) break;

        let fullyHidden = this.scroller.scrollLeft >= stride + (this.edgeFade || 0);
        if (typeof firstCard.getBoundingClientRect === 'function' && typeof this.scroller.getBoundingClientRect === 'function') {
          fullyHidden = firstCard.getBoundingClientRect().right <= this.scroller.getBoundingClientRect().left;
        }
        if (!fullyHidden) break;
        if (typeof firstCard.contains === 'function' && firstCard.contains(document.activeElement)) break;

        const previousScroll = this.scroller.scrollLeft;
        this.track.append(firstCard);
        this.autoplayScrollDeadline = Date.now() + AUTOPLAY_SCROLL_GUARD;
        this.scroller.scrollLeft = Math.max(0, previousScroll - stride);
        moved += 1;
      }

      this.normalizingScroll = false;
    }

    ensureReverseBuffer() {
      if (!this.cardStride || this.normalizingScroll || this.track.children.length < 2) return;
      if (this.scroller.scrollLeft > this.cardStride + this.edgeFade) return;

      this.normalizingScroll = true;
      const lastCard = this.track.lastElementChild;
      if (lastCard && !(typeof lastCard.contains === 'function' && lastCard.contains(document.activeElement))) {
        const previousScroll = Math.max(0, this.scroller.scrollLeft);
        this.track.prepend(lastCard);
        this.scroller.scrollLeft = previousScroll + this.cardStride;
      }
      this.normalizingScroll = false;
    }

    normalizeReverseOverscroll() {
      if (!this.interactionPaused || this.pointerActive || this.normalizingScroll) return;
      if (this.scroller.scrollLeft > 1) return;
      this.ensureReverseBuffer();
    }

    onPointerEnter(event) {
      if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      this.hoverPaused = true;
      this.syncMotion();
    }

    onPointerLeave(event) {
      if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      this.hoverPaused = false;
      this.syncMotion();
    }

    onPointerDown(event) {
      if (event.isPrimary === false || this.pointerActive) return;
      this.pointerActive = true;
      this.activePointerId = typeof event.pointerId === 'number' ? event.pointerId : null;
      this.autoplayScrollDeadline = 0;
      this.beginInteraction();
    }

    onPointerEnd(event) {
      if (event?.isPrimary === false || !this.pointerActive) return;
      const pointerId = typeof event?.pointerId === 'number' ? event.pointerId : null;
      if (this.activePointerId !== null && pointerId !== null && pointerId !== this.activePointerId) return;
      this.pointerActive = false;
      this.activePointerId = null;
      this.beginInteraction();
      this.scheduleResume();
    }

    onWheel(event) {
      this.autoplayScrollDeadline = 0;
      this.beginInteraction();
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && event.deltaX < 0) {
        this.ensureReverseBuffer();
      }
      this.scheduleResume();
    }

    onScroll() {
      this.updateProgress();
      if (this.normalizingScroll || Date.now() <= this.autoplayScrollDeadline) return;

      this.beginInteraction();
      this.normalizeReverseOverscroll();
      this.scheduleResume();
    }

    onFocusIn(event) {
      this.focusPaused = event.target !== this.pauseButton;
      this.syncMotion();
    }

    onFocusOut(event) {
      this.focusPaused = Boolean(
        event.relatedTarget
        && this.contains(event.relatedTarget)
        && event.relatedTarget !== this.pauseButton
      );
      this.syncMotion();
    }

    onPurchaseInteraction(event) {
      if (event.target === this.pauseButton || this.pauseButton?.contains?.(event.target)) return;
      if (!event.target.closest?.('a, button, input, select, textarea, product-form')) return;
      this.beginInteraction();
      this.scheduleResume();
    }

    onSubmitInteraction() {
      this.beginInteraction();
      this.scheduleResume();
    }

    onKeydown(event) {
      if (event.target.matches?.('input, select, textarea')) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.beginInteraction();
      if (event.key === 'ArrowLeft') this.ensureReverseBuffer();
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      const distance = (this.cardStride || this.scroller.clientWidth * 0.8) * direction;
      this.scroller.scrollBy({
        left: distance,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.scheduleResume();
    }

    beginInteraction() {
      if (this.resumeTimer !== null) {
        window.clearTimeout(this.resumeTimer);
        this.resumeTimer = null;
      }
      this.interactionPaused = true;
      this.syncMotion();
    }

    scheduleResume() {
      if (this.resumeTimer !== null) window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        this.resumeTimer = null;
        if (!this.isConnected) return;
        this.interactionPaused = false;
        this.syncMotion();
      }, RESUME_DELAY);
    }

    togglePause() {
      this.manualPaused = !this.manualPaused;
      this.updatePauseControl();
      this.syncMotion();
    }

    updatePauseControl() {
      if (!this.pauseButton) return;

      const paused = this.manualPaused || !this.autoplayEnabled;
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.disabled = !this.autoplayEnabled;
      if (this.pauseLabel) this.pauseLabel.textContent = paused ? 'Play' : 'Pause';
      if (this.pauseIcon) this.pauseIcon.hidden = paused;
      if (this.playIcon) this.playIcon.hidden = !paused;
    }

    updateProgress() {
      if (!this.progress || !this.track?.firstElementChild) return;

      const firstIndex = Number.parseInt(this.track.firstElementChild.dataset.saleTideOriginalIndex, 10) || 0;
      const count = this.track.children.length;
      const stride = this.cardStride || (this.track.firstElementChild.offsetWidth + this.cardGap);
      const fractionalPosition = stride > 0 ? Math.max(0, this.scroller.scrollLeft - this.edgeFade) / stride : 0;
      const collectionPosition = ((firstIndex + fractionalPosition) % count + count) % count;
      const progressValue = count > 1 ? collectionPosition / (count - 1) : 0;
      this.progress.style.setProperty('--sale-tide-progress', String(Math.min(1, progressValue)));
    }
  }

  customElements.define('sale-tide-feature', SaleTideFeature);
})();
